import { useCallback, useEffect, useState } from "react";

import { useLabels } from "~/hooks/useLabels";
import { isMobileNative } from "~/utils/helpers";
import { toPx } from "~/utils/components";
import { useBreakpoints } from "~/hooks/useBreakpoints";

import { Box } from "../box/Box";
import { Button } from "../button/Button";
import { Heading } from "../heading/Heading";
import { HtmlContent } from "../htmlContent/HtmlContent";
import { SheetModal } from "../sheetModal/SheetModal";
import { Text } from "../text/Text";
import { TextEditor } from "../textEditor/TextEditor";
import { DialogModal } from "../dialogModal/DialogModal";

import "./RichTextCard.css";

import type { SyntheticEvent, ReactNode } from "react";
import type { TextEditorSaveEvent } from "../textEditor/TextEditor";

export interface RichTextCardData {
  value: string;
}

export interface RichTextCardSaveEvent extends RichTextCardData {}

export interface RichTextCardProps {
  useSimpleEditor?: boolean;
  editorPostTitle?: ReactNode;
  editorTitle?: ReactNode;
  limit?: number;
  placeholder?: string;
  value?: string;
  valueHtml?: string;
  onRequestEdit?: (event: SyntheticEvent) => void;
  onSave?: (event: RichTextCardSaveEvent) => Promise<void>;
}

const DEFAULT_LABELS = {
  "textEditor.heading.reflection": "Reflection Question",
  "textEditor.placeholder.reflection": "Write a Response…",
};

export function RichTextCard({
  useSimpleEditor = false,
  editorPostTitle,
  editorTitle,
  limit,
  placeholder,
  value,
  valueHtml,
  onRequestEdit,
  onSave,
}: RichTextCardProps) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });
  const breakpoints = useBreakpoints();

  const [isWriting, setIsWriting] = useState(false);

  // The size of the virtual keyboard
  const [insetBottom, setInsetBottom] = useState<number>(0);

  const style = {
    "--html-content-line-height": "var(--line-height-snug)",
  };

  const dialogModalCustomProperties = {
    "--dialog-modal-body-padding": "0 var(--size-1-5) var(--size-1-5)",
  };

  const sheetModalCustomProperties = {
    "--sheet-modal-inset-bottom": insetBottom ? toPx(insetBottom) : undefined,
    "--sheet-modal-z-index": 82,
  };

  const computedPlaceholder =
    placeholder ?? t("textEditor.placeholder.reflection");

  const handleInputClick = (e: SyntheticEvent) => {
    onRequestEdit?.(e);

    if (!e.defaultPrevented) {
      setIsWriting(true);
    }
  };

  const handleSave = useCallback(
    async (event: TextEditorSaveEvent) => {
      try {
        await onSave?.({ value: event.value });
        setIsWriting(false);
      } catch (error) {}
    },
    [onSave],
  );

  const handleRequestClose = useCallback(() => {
    setIsWriting(false);
  }, []);

  const renderInputValue = useCallback(() => {
    if (value) {
      return <HtmlContent html={valueHtml} />;
    }

    return <Text color="gray">{computedPlaceholder}</Text>;
  }, [computedPlaceholder, value, valueHtml]);

  const renderHeader = useCallback(() => {
    return (
      <div className="RichTextCard__modal-header">
        <Box justify={"space-between"} align="center">
          <Heading level="3" displayLevel="6">
            {editorTitle}
          </Heading>

          <Button
            shape="circle"
            icon="xmark"
            appearance="minimal"
            size="xs"
            onClick={handleRequestClose}
          />
        </Box>

        {editorPostTitle && (
          <div className="RichTextCard__post-title">{editorPostTitle}</div>
        )}
      </div>
    );
  }, [editorPostTitle, editorTitle, handleRequestClose]);

  const renderEditor = useCallback(() => {
    return (
      <div className="RichTextCard__editor">
        <TextEditor
          useSimpleEditor={useSimpleEditor}
          autoFocus
          limit={limit}
          initialValue={value}
          placeholder={computedPlaceholder}
          onSave={handleSave}
        />
      </div>
    );
  }, [computedPlaceholder, handleSave, limit, useSimpleEditor, value]);

  // Mobile Navtive event listeners
  useEffect(() => {
    if (isMobileNative()) {
      const handleKeyboard = (e: CustomEventInit) => {
        setInsetBottom(e.detail.height);
      };

      document.addEventListener("classroom:keyboardWillShow", handleKeyboard);
      document.addEventListener("classroom:keyboardWillHide", handleKeyboard);

      return () => {
        document.removeEventListener(
          "classroom:keyboardWillShow",
          handleKeyboard,
        );
        document.removeEventListener(
          "classroom:keyboardWillHide",
          handleKeyboard,
        );
      };
    }
  }, []);

  return (
    <div className="RichTextCard" style={style}>
      <div className="RichTextCard__input" onClick={handleInputClick}>
        {renderInputValue()}
      </div>

      {breakpoints.lg ? (
        <DialogModal
          isOpen={isWriting}
          header={renderHeader()}
          style={dialogModalCustomProperties}
          onRequestClose={handleRequestClose}
        >
          <DialogModal.Body>{renderEditor()}</DialogModal.Body>
        </DialogModal>
      ) : (
        <SheetModal
          disableGrabber
          disableGestures
          style={sheetModalCustomProperties}
          header={renderHeader()}
          isOpen={isWriting}
          size="lg"
          onRequestClose={handleRequestClose}
        >
          {renderEditor()}
        </SheetModal>
      )}
    </div>
  );
}
